import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  patchSaTenantSesAccount,
  postSaTenantSesAccountHealth,
  postSaTenantByoCircuitResetRecommended,
  SaTenantHealth,
  SaTenantSesByoHealth,
  SaTenantSesByoPayload,
} from "../../../helpers/super_admin_ops_helper";
import { BYO_GUIDE_STEPS } from "../Ses/ByoSetupGuide";

type Props = {
  tenant: SaTenantHealth;
  onUpdated: (tenant: SaTenantHealth) => void;
};

/**
 * Card SA: liga BYO + grava keys + checklist + probe health AWS (DOC 14 § 8.4).
 */
const SesByoCard = ({ tenant, onUpdated }: Props) => {
  const { t } = useTranslation();
  const byo = tenant.ses_byo;
  const account = byo?.account;

  const [mode, setMode] = useState<"platform" | "customer_aws">("platform");
  const [region, setRegion] = useState("us-east-1");
  const [configSet, setConfigSet] = useState("");
  const [status, setStatus] = useState<"draft" | "ready" | "disabled">("draft");
  const [accessKey, setAccessKey] = useState("");
  const [secret, setSecret] = useState("");
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);
  const [resetCircuitBusy, setResetCircuitBusy] = useState(false);
  const [lastProbe, setLastProbe] = useState<SaTenantSesByoHealth | null>(null);

  useEffect(() => {
    const m =
      byo?.sending_account_mode === "customer_aws"
        ? "customer_aws"
        : "platform";
    setMode(m);
    setRegion(account?.region || "us-east-1");
    setConfigSet(account?.configuration_set || "");
    setStatus(
      (account?.status as "draft" | "ready" | "disabled") || "draft"
    );
    setNotes(account?.notes || "");
    setChecklist(account?.ops_checklist || {});
    setAccessKey("");
    setSecret("");
    // Restaura último probe persistido (sem re-chamar AWS).
    if (account?.last_health_at && account.last_health_summary) {
      setLastProbe({
        ok: !!account.last_health_ok,
        checked_at: account.last_health_at,
        region: account.region,
        configuration_set: account.configuration_set,
        access_key_last4: account.access_key_last4,
        checks: account.last_health_summary.checks || [],
        aws_error_code: account.last_health_summary.aws_error_code ?? null,
      });
    } else {
      setLastProbe(null);
    }
  }, [tenant.id, byo?.sending_account_mode, account?.updated_at, account?.last_health_at]);

  const doneCount = useMemo(
    () => BYO_GUIDE_STEPS.filter((s) => checklist[s]).length,
    [checklist]
  );

  const canProbe =
    mode === "customer_aws" &&
    !!account?.has_secret &&
    !!account?.has_access_key &&
    !!configSet.trim();

  const save = async () => {
    setBusy(true);
    try {
      const payload: SaTenantSesByoPayload = {
        sending_account_mode: mode,
        region,
        configuration_set: configSet || null,
        status,
        ops_checklist: checklist,
        notes: notes || null,
      };
      if (accessKey.trim() !== "") {
        payload.aws_access_key_id = accessKey.trim();
      }
      if (secret.trim() !== "") {
        payload.aws_secret_access_key = secret.trim();
      }
      const res = await patchSaTenantSesAccount(tenant.id, payload);
      if (!res?.success) {
        toast.error(t("sa.sesByo.tenant.error"));
        return;
      }
      onUpdated(res.data);
      toast.success(t("sa.sesByo.tenant.saved"));
    } catch (e: any) {
      toast.error(e?.message || t("sa.sesByo.tenant.error"));
    } finally {
      setBusy(false);
    }
  };

  const runHealth = async () => {
    setHealthBusy(true);
    try {
      const res = await postSaTenantSesAccountHealth(tenant.id);
      if (!res?.success) {
        toast.error(t("sa.sesByo.tenant.healthError"));
        return;
      }
      setLastProbe(res.data.health);
      onUpdated(res.data.tenant);
      if (res.data.health.ok) {
        toast.success(t("sa.sesByo.tenant.healthOk"));
      } else {
        toast.warning(t("sa.sesByo.tenant.healthFailed"));
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.sesByo.tenant.healthError"));
    } finally {
      setHealthBusy(false);
    }
  };

  const resetCircuitRecommended = async () => {
    setResetCircuitBusy(true);
    try {
      const res = await postSaTenantByoCircuitResetRecommended(tenant.id);
      if (!res?.success) {
        toast.error(t("sa.sesByo.tenant.circuitResetError"));
        return;
      }
      onUpdated(res.data);
      toast.success(t("sa.sesByo.tenant.circuitResetOk"));
    } catch (e: any) {
      toast.error(e?.message || t("sa.sesByo.tenant.circuitResetError"));
    } finally {
      setResetCircuitBusy(false);
    }
  };

  const toggleStep = (step: string) => {
    setChecklist((prev) => ({ ...prev, [step]: !prev[step] }));
  };

  const checkLabel = (name: string) => {
    const key = `sa.sesByo.tenant.healthCheck.${name}`;
    const translated = t(key);
    return translated === key ? name : translated;
  };

  return (
    <Card id="ses-byo" className="mb-3">
      <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h5 className="card-title mb-0">{t("sa.sesByo.tenant.cardTitle")}</h5>
        <div className="d-flex gap-2 align-items-center">
          {mode === "customer_aws" && (
            <Badge color={account?.is_ready ? "success" : "warning"}>
              {account?.is_ready
                ? t("sa.sesByo.tenant.readyBadge")
                : t("sa.sesByo.tenant.draftBadge")}
            </Badge>
          )}
          <Button
            color="soft-primary"
            size="sm"
            tag={Link}
            to={`/super-admin/ses/byo-guide?tenantId=${tenant.id}`}
          >
            {t("sa.sesByo.tenant.openGuide")}
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <Alert color="info" className="mb-3">
          {t("sa.sesByo.guide.notSelfServe")}
        </Alert>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>{t("sa.sesByo.tenant.mode")}</Label>
              <Input
                type="select"
                value={mode}
                onChange={(e) =>
                  setMode(e.target.value as "platform" | "customer_aws")
                }
              >
                <option value="platform">
                  {t("sa.sesByo.tenant.modePlatform")}
                </option>
                <option value="customer_aws">
                  {t("sa.sesByo.tenant.modeCustomer")}
                </option>
              </Input>
            </FormGroup>
          </Col>
          {mode === "customer_aws" && (
            <Col md={6}>
              <FormGroup>
                <Label>{t("sa.sesByo.tenant.status")}</Label>
                <Input
                  type="select"
                  value={status}
                  onChange={(e) =>
                    setStatus(
                      e.target.value as "draft" | "ready" | "disabled"
                    )
                  }
                >
                  <option value="draft">
                    {t("sa.sesByo.tenant.statusDraft")}
                  </option>
                  <option value="ready">
                    {t("sa.sesByo.tenant.statusReady")}
                  </option>
                  <option value="disabled">
                    {t("sa.sesByo.tenant.statusDisabled")}
                  </option>
                </Input>
              </FormGroup>
            </Col>
          )}
        </Row>

        {mode === "customer_aws" && (
          <>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>{t("sa.sesByo.tenant.region")}</Label>
                  <Input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="us-east-1"
                  />
                </FormGroup>
              </Col>
              <Col md={8}>
                <FormGroup>
                  <Label>{t("sa.sesByo.tenant.configSet")}</Label>
                  <Input
                    value={configSet}
                    onChange={(e) => setConfigSet(e.target.value)}
                    placeholder="brenvio-cliente"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("sa.sesByo.tenant.accessKey")}</Label>
                  <Input
                    value={accessKey}
                    onChange={(e) => setAccessKey(e.target.value)}
                    placeholder={
                      account?.access_key_last4
                        ? `…${account.access_key_last4}`
                        : ""
                    }
                    autoComplete="off"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("sa.sesByo.tenant.secret")}</Label>
                  <Input
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    placeholder={account?.has_secret ? "••••••••" : ""}
                    autoComplete="new-password"
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>{t("sa.sesByo.tenant.notes")}</Label>
              <Input
                type="textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </FormGroup>

            <h6 className="mb-2">
              {t("sa.sesByo.tenant.checklist")}{" "}
              <span className="text-muted small">
                ({doneCount}/{BYO_GUIDE_STEPS.length})
              </span>
            </h6>
            <div className="mb-3" style={{ maxHeight: 220, overflowY: "auto" }}>
              {BYO_GUIDE_STEPS.map((step) => (
                <FormGroup check key={step} className="mb-1">
                  <Input
                    id={`byo-step-${step}`}
                    type="checkbox"
                    checked={!!checklist[step]}
                    onChange={() => toggleStep(step)}
                  />
                  <Label check for={`byo-step-${step}`} className="small">
                    {t(`sa.sesByo.guide.steps.${step}.title`)}
                  </Label>
                </FormGroup>
              ))}
            </div>

            {lastProbe && (
              <Alert
                color={lastProbe.ok ? "success" : "danger"}
                className="mb-3"
              >
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                  <strong>
                    {lastProbe.ok
                      ? t("sa.sesByo.tenant.healthOk")
                      : t("sa.sesByo.tenant.healthFailed")}
                  </strong>
                  <span className="small text-muted">
                    {t("sa.sesByo.tenant.healthCheckedAt", {
                      at: lastProbe.checked_at,
                    })}
                  </span>
                </div>
                <ul className="mb-0 small ps-3">
                  {lastProbe.checks.map((c) => (
                    <li key={c.name}>
                      <Badge
                        color={c.ok ? "success" : "danger"}
                        className="me-1"
                      >
                        {c.ok ? "OK" : "Falha"}
                      </Badge>
                      {checkLabel(c.name)}
                      {c.detail ? ` — ${c.detail}` : ""}
                    </li>
                  ))}
                </ul>
                {lastProbe.aws_error_code && (
                  <p className="mb-0 mt-2 small text-muted">
                    AWS: {lastProbe.aws_error_code}
                  </p>
                )}
              </Alert>
            )}

            {byo?.circuit?.available && byo.circuit.effective && (
              <Alert
                color={byo.circuit.near_aws_ceiling ? "danger" : "secondary"}
                className="mb-3"
              >
                <strong className="d-block mb-1">
                  {t("sa.sesByo.tenant.circuitTitle")}
                </strong>
                {byo.circuit.near_aws_ceiling && (
                  <p className="mb-2 small fw-medium">
                    {t("sa.sesByo.tenant.circuitNearCeiling")}
                  </p>
                )}
                <ul className="mb-1 small ps-3">
                  <li>
                    {t("sa.sesByo.tenant.circuitBounce", {
                      value: byo.circuit.effective.bounce_pct,
                    })}
                  </li>
                  <li>
                    {t("sa.sesByo.tenant.circuitComplaint", {
                      value: byo.circuit.effective.complaint_pct,
                    })}
                  </li>
                  <li>
                    {t("sa.sesByo.tenant.circuitMinSent", {
                      value: byo.circuit.effective.min_sent,
                    })}
                  </li>
                  <li>
                    {t(
                      byo.circuit.effective.auto_resume_enabled
                        ? "sa.sesByo.tenant.circuitAutoOn"
                        : "sa.sesByo.tenant.circuitAutoOff"
                    )}
                  </li>
                </ul>
                <p className="mb-0 small text-muted">
                  {t("sa.sesByo.tenant.circuitSource", {
                    source:
                      byo.circuit.effective.source === "byo_override"
                        ? t("sa.sesByo.tenant.circuitSourceOverride")
                        : t("sa.sesByo.tenant.circuitSourcePlatform"),
                  })}
                </p>
                {byo.circuit.effective.source === "byo_override" && (
                  <Button
                    color="soft-warning"
                    size="sm"
                    className="mt-2"
                    disabled={resetCircuitBusy}
                    onClick={() => void resetCircuitRecommended()}
                  >
                    {resetCircuitBusy ? (
                      <Spinner size="sm" />
                    ) : (
                      t("sa.sesByo.tenant.circuitResetRecommended")
                    )}
                  </Button>
                )}
              </Alert>
            )}
          </>
        )}

        <div className="d-flex flex-wrap gap-2">
          <Button color="primary" size="sm" disabled={busy} onClick={save}>
            {busy ? <Spinner size="sm" /> : t("sa.sesByo.tenant.save")}
          </Button>
          {mode === "customer_aws" && (
            <Button
              color="soft-success"
              size="sm"
              disabled={healthBusy || busy || !canProbe}
              onClick={runHealth}
              title={
                canProbe
                  ? undefined
                  : t("sa.sesByo.tenant.healthNeedSave")
              }
            >
              {healthBusy ? (
                <Spinner size="sm" />
              ) : (
                t("sa.sesByo.tenant.healthRun")
              )}
            </Button>
          )}
        </div>
        {mode === "customer_aws" && !canProbe && (
          <p className="text-muted small mt-2 mb-0">
            {t("sa.sesByo.tenant.healthNeedSave")}
          </p>
        )}
      </CardBody>
    </Card>
  );
};

export default SesByoCard;
